import Button from 'components/Button';
import Field from 'components/Form/Field';
import {RequiredType} from 'components/Form/types';
import Icon from 'components/Icon';
import {
    ChangeEvent,
    ComponentProps,
    FC,
    InputHTMLAttributes,
    ReactNode,
    useRef,
} from 'react';
import {FieldError} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';
import {c} from 'ttag';

import {useOnDragOver} from './hooks';

export const FileUploadContainer: FC<
    {
        error?: FieldError;
        onChange: (obj: File[] | null) => void;
        onReset: () => void;
        value?: File | null;
    } & Omit<
        InputHTMLAttributes<HTMLInputElement>,
        'onChange' | 'type' | 'value'
    >
> = ({className, disabled, error, onChange, onReset, value, ...inputProps}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const {isDraggedOver, ...dragProps} = useOnDragOver(onChange, {disabled});

    return (
        <div
            className={twMerge(
                'flex flex-wrap gap-2',
                'h-min w-full items-center justify-center p-3',
                'rounded border-2 border-dashed border-grey-300 ',
                error && 'border-coral-500 bg-coral-100',
                isDraggedOver && 'bg-grey-200 border-grey-300',
                className,
            )}
            data-error={!!error}
            role="region"
            {...dragProps}
        >
            <div className="inline-flex items-center gap-2">
                <span
                    className={twMerge(
                        'whitespace-nowrap',
                        isDraggedOver || disabled
                            ? 'text-light-disabled'
                            : 'text-light-secondary',
                    )}
                >
                    {c('component').t`Drag and drop your file here or`}
                </span>
                <Button
                    disabled={isDraggedOver || disabled}
                    onClick={() => {
                        if (!disabled && inputRef.current !== null) {
                            inputRef.current.click();
                        }
                    }}
                    size="sm"
                >
                    {c('component').t`Browse`}
                </Button>
            </div>
            {!value && (
                <span
                    className={twMerge(
                        'whitespace-nowrap',
                        isDraggedOver || disabled
                            ? 'text-light-disabled'
                            : 'text-light-secondary',
                    )}
                >
                    {c('component').t`No file chosen`}
                </span>
            )}
            {value && (
                <button
                    className={twMerge(
                        'inline-flex items-center truncate rounded bg-grey-200 px-2 py-0.5',
                        !disabled &&
                            'hover:bg-coral-100 active:bg-coral-200 hover:text-coral-700',
                        disabled && 'pointer-events-none',
                        disabled && 'text-light-disabled',
                        !disabled && 'text-light-primary',
                    )}
                    onClick={() => {
                        if (inputRef.current) {
                            inputRef.current.value = '';
                        }
                        onReset();
                    }}
                    type="button"
                >
                    <span
                        className="text-wrap"
                        style={{overflowWrap: 'anywhere'}}
                    >
                        {value.name}
                    </span>
                    <Icon icon="close_small" size="base" />
                </button>
            )}
            <input
                hidden={true}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    return onChange(Array.from(event.target.files || []));
                }}
                ref={inputRef}
                type="file"
                {...inputProps}
            />
        </div>
    );
};

export const FileUploadField: FC<
    {
        classNameUpload?: string;
        helpText?: ReactNode;
        label?: ReactNode;
        name: string;
        requiredType?: RequiredType;
    } & ComponentProps<typeof FileUploadContainer>
> = ({
    className,
    classNameUpload,
    disabled,
    error,
    helpText,
    label,
    name,
    onChange,
    onReset,
    required,
    requiredType,
    value,
    ...inputProps
}) => {
    return (
        <Field
            className={className}
            disabled={disabled}
            error={error}
            helpText={helpText}
            label={label}
            name={name}
            required={required}
            requiredType={requiredType}
            type="input"
        >
            <FileUploadContainer
                className={classNameUpload}
                disabled={disabled}
                error={error}
                onChange={onChange}
                onReset={onReset}
                value={value}
                {...inputProps}
            />
        </Field>
    );
};
